import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const VideoCard = ({ title, caption, slug, mediaId }) => (
  <article className="flex flex-col" data-testid={`video-${slug}`}>
    <span className="font-condensed uppercase tracking-widest text-[14px] text-lime mb-3">
      {title}
    </span>
    <WistiaPlayer mediaId={mediaId} aspect={0.8} />
    <p className="mt-4 text-[14px] leading-[1.55] opacity-80">{caption}</p>
  </article>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[44ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico whitespace-nowrap"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[22ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

const QualBlock = ({ title, body }) => (
  <div>
    <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
      {title}
    </h4>
    <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
      {body}
    </p>
  </div>
);

const GALLERY = [
  {
    src: "/images/assets/iqb8v7hd_free_macbook_pro_1-scaled.jpg",
    alt: "Onboarding AC Milan Connect su desktop",
  },
  {
    src: "/images/assets/puj1qo50_milanverse-1.jpg",
    alt: "ConnectVerse — area contenuti premium",
  },
  {
    src: "/images/assets/etcial1m_frame-5.jpg",
    alt: "Key visual — Welcome to AC Milan Connect",
  },
  {
    src: "/images/assets/gsvgr6p9_1698141384711.jpg",
    alt: "Pre-lancio a San Siro — Milan vs Juventus",
  },
  {
    src: "/images/assets/gtvso0dz_2023.11.30-afinna-acmilanconnect-webhp.webp",
    alt: "Welcome kit con SIM fisica AC Milan Connect",
  },
];

const AcMilanConnect = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const idx = WORKS.findIndex((w) => w.to === "/works/ac-milan-connect");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = WORKS[(idx + 1) % WORKS.length];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "AC Milan Connect", url: "/works/ac-milan-connect" },
  ]);

  return (
    <>
      <Seo
        title="AC Milan Connect: 2.774 pre-iscritti in 48 ore | OnSports Case"
        description="Lancio AC Milan Connect, la SIM dei tifosi rossoneri: 2.774 pre-iscritti in 48 ore, 1.000+ SIM vendute, CPL €10. Performance marketing OnSports per AC Milan."
        path="/works/ac-milan-connect"
        image="/images/assets/gsvgr6p9_1698141384711.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/ac-milan-connect/hero.jpg"
        imageAlt="AC Milan Connect — AC Milan × Afinna One"
        eyebrow="● Case study · Business Impact · Strategy · Content · Performance"
        titleLines={["Dalla sponsorship alla", "CONNESSIONE."]}
        highlightWord="CONNESSIONE."
        subtitle="Abbiamo guidato il lancio di AC Milan Connect, la prima SIM al mondo brandizzata da un club di calcio. Una sponsorship trasformata in un prodotto che ha generato awareness, lead e ricavi reali — fin dalla prima partita."
        meta="2023/2024  ·  Cliente — AC Milan × Afinna One  ·  Categoria — Strategy · Content · Performance"
        overlayOpacity={0.6}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    AC Milan Connect è la prima SIM telefonica mobile dedicata ai tifosi
                    di un club di calcio. Nata dalla partnership tra AC Milan e Afinna
                    One — operatore virtuale italiano per le telecomunicazioni — porta i
                    tifosi rossoneri dentro un ecosistema che unisce connettività di
                    alta qualità, offerte dedicate e accesso esclusivo a ConnectVerse:
                    la piattaforma di contenuti premium del club.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Un progetto unico nel panorama internazionale: il Milan è stato il
                    primo club al mondo a strutturare un servizio di telefonia mobile a
                    marchio club, trasformando una sponsorship telco in un prodotto
                    consumer end-to-end con SIM fisica, area riservata, contenuti video
                    esclusivi, eventi dal vivo e programma di loyalty.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">AC Milan Connect + ConnectVerse</SchedaRow>
                    <SchedaRow label="Clienti">AC Milan · Afinna One</SchedaRow>
                    <SchedaRow label="Pilastro">
                      Business Impact · Content &amp; IP Studio
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Strategia di lancio · Concept creativo<br />
                      Performance marketing · Eventi · UX/UI
                    </SchedaRow>
                    <SchedaRow label="Anno">Ottobre 2023 — in corso</SchedaRow>
                    <SchedaRow label="Primato">
                      Primo club di calcio al mondo<br />
                      con SIM mobile dedicata
                    </SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Lanciare un prodotto che non esisteva.</h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Quando AC Milan e Afinna One ci hanno coinvolti, la sponsorship era
                  già firmata, ma il prodotto era ancora un'idea. Servivano tre cose
                  simultanee: costruire un posizionamento credibile per una SIM
                  brandizzata da un club di calcio (categoria inesistente, nessun
                  benchmark), generare domanda prima ancora di avere il prodotto a
                  scaffale, e farlo dentro il calendario competitivo di una stagione di
                  Serie A, dove ogni big match è una finestra di visibilità che non si
                  può perdere.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  La sfida vera non era comunicare un nuovo telco: era convincere
                  milioni di tifosi rossoneri che una SIM potesse essere una forma di
                  tifo. Trasformare un contratto di sponsorizzazione in un'esperienza
                  riconoscibile, desiderabile e — soprattutto — comprabile.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL LANCIO (VIDEO) */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-lancio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Il lancio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Due momenti. Una sola storia.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch]">
                  Abbiamo costruito il lancio su due asset video chiave: il film
                  istituzionale che ha presentato AC Milan Connect al mondo, e il video
                  dell'evento di consegna a Casa Milan, dove le prime 250 SIM sono state
                  attivate insieme alle leggende del club. Stesso linguaggio, due
                  funzioni narrative diverse: presentare il prodotto, e provarne il
                  valore esperienziale.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <RevealOnScroll delay={0.05}>
              <VideoCard
                title="Video di lancio"
                caption="Il film istituzionale che ha introdotto AC Milan Connect ai tifosi rossoneri."
                slug="lancio"
                mediaId="6e5t5qy24l"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <VideoCard
                title="Evento Casa Milan"
                caption="22 novembre 2023 — l'attivazione delle prime SIM con Dida e Serginho."
                slug="evento"
                mediaId="4tr6ivpy9c"
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Una sponsorship trasformata in prodotto.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
                  Abbiamo costruito una go-to-market integrata che ha unito strategia,
                  creatività, eventi e performance marketing in un'unica regia.
                  L'obiettivo non era solo annunciare un prodotto, ma creare un funnel
                  di marketing completo — dall'awareness all'acquisto, fino alla loyalty
                  — coordinando tre stakeholder con esigenze diverse: il club,
                  l'operatore telco e i tifosi.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Strategy & Go-to-Market"
                body="Posizionamento, customer journey, funnel marketing across awareness-consideration-acquisto-retention-advocacy. Piano media integrato online/offline coordinato con il calendario sportivo."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Concept & Content"
                body="Concept creativi nati dal mondo Milan: “The Call” ispirato all'esultanza di Leão, “Who you gonna call?”, “ConnectVerse” come piattaforma editoriale. Storytelling che ha trasformato una SIM in un gesto di appartenenza."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Eventi & Attivazioni"
                body="Pre-lancio in-stadium durante Milan-Juventus (LED, video, speaker, attivazioni), evento Casa Milan con le leggende per la consegna delle prime SIM, Stadium Experience per i clienti top."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="Performance Marketing"
                body="Campagne paid su Meta, TikTok, Google, YouTube con prospecting e retargeting. Landing page dedicate, ottimizzazione progressiva del funnel, reporting in tempo reale su CPL e CAC."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 6 — IL MOMENTO CHIAVE */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-momento">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>22 ottobre 2023 · San Siro</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Quando uno stadio è diventato un canale di marketing.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[720px]">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il pre-lancio è andato in scena durante Milan-Juventus, davanti a
                  75.000 persone a San Siro e milioni di spettatori in TV. LED a bordo
                  campo, video istituzionale prima del match e all'intervallo, annuncio
                  dello speaker, attivazioni nei pre e post-partita, PR coordinata con
                  le testate sportive nazionali, banner sul sito ufficiale acmilan.com,
                  DEM ai membri della newsletter del club.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  Il risultato si è visto in poche ore: AC Milan Connect è entrato nei
                  trend Google correlati al Milan con la crescita più alta tra tutti i
                  trend del mondo Milan. In meno di 48 ore, 2.774 persone si erano
                  pre-iscritte per ricevere l'offerta. Senza un solo euro di adv pagata.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">I numeri di un primato.</h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="1°" label="Club di calcio al mondo con SIM dedicata" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="2.774" label="Pre-iscritti in 48 ore dal pre-lancio" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="1.000+" label="SIM vendute al lancio in organico" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="€10" label="Costo per lead dopo l'ottimizzazione delle campagne" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Dati al primo periodo di lancio (ottobre 2023 — aprile 2024). 1.692 registrati
            area utenti e 1.109 SIM vendute al 4 dicembre 2023, generati senza
            investimento pubblicitario. €10 CPL e €40 CAC su SIM attivata misurati
            durante il test di campagna marzo-aprile 2024.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 text-galactico">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3">
                  Trend culturale
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  Durante e nei due giorni successivi a Milan-Juventus, "AC Milan
                  Connect" è entrato nelle ricerche correlate a "AC Milan" con la
                  crescita più alta tra tutti i trend del mondo Milan. Un risultato
                  culturale che ha anticipato quello commerciale.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3">
                  Vendita senza adv
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  Le prime 1.000 SIM sono state vendute interamente attraverso attività
                  organiche: stadio, social, DEM, sito ufficiale, eventi. Prova concreta
                  che una sponsorship ben costruita può essere un canale di vendita, non
                  solo di visibilità.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3">
                  Unit economics validate
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  Il test successivo di performance marketing su Meta, TikTok, Google e
                  YouTube ha confermato unit economics sostenibili per il segmento telco
                  (€10 CPL, €40 CAC), aprendo lo scaling delle campagne nelle stagioni
                  successive.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 8 — GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto, in immagini.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {GALLERY.map((img, i) => (
              <RevealOnScroll key={i} delay={i * 0.05}>
                <button
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="block w-full aspect-[4/3] overflow-hidden relative group focus:outline-none"
                  data-testid={`gallery-${i}`}
                  data-hover="true"
                  aria-label={`Apri: ${img.alt}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    draggable="false"
                  />
                  <span
                    className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-hidden="true"
                  />
                </button>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      <Lightbox
        images={GALLERY}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 9 — CREDITS */}
      <section className="py-20 md:py-28 bg-dodgers text-galactico" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Clienti">AC Milan · Afinna One</SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Strategia di lancio, concept creativo,<br />
                  performance marketing, eventi, UX/UI
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Impact Strategy · Content &amp; IP Studio<br />
                  Performance Marketing &amp; Growth
                </SchedaRow>
                <SchedaRow label="Periodo">Ottobre 2023 — in corso</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — CTA FINALE */}
      <section className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10" data-testid="section-cta-finale">
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[26ch]">
              Hai una sponsorship che vuoi trasformare in un prodotto? Progettiamo
              insieme il suo <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[60ch]">
              Lavoriamo con club, brand e operatori che vogliono uscire dalla logica
              del logo sulla maglia per costruire piattaforme di valore reale.
              Raccontaci dove vuoi arrivare.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 11 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AcMilanConnect;
